import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import useSWR from 'swr';
import { Bookmark } from '../../../../../../../typings/Bookmark.interface';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { BookmarksFiltersProps, BookmarksQueryParams } from '../../../../typings/Bookmarks.interface';
import { extractFromArray } from '../../../../utils';
import Button from '../../../common/base/Button';
import EmptyState from '../../../common/EmptyState';
import { useToasts } from '../../../common/Toast';
import { GlobalContext } from '../../../contexts/GlobalContext';
import useSearchQuery from '../../../hooks/useSearchQuery';
import { StyledViewContent, StyledViewLayout, StyledViewNav, StyledViewNavActions, StyledViewNavHeader, ViewTitle } from '../../../layouts/ViewLayout';
import { DashboardContext } from '../DashboardContext';
import EventSidebar from '../Events/EventSidebar';
import BookmarkDetails from './BookmarkDetails';
import BookmarksList from './BookmarksList';
import { EventWithData } from '../../../../../../../typings/Event.interface';
import LINKS from '../../../../configs/links';
const extractFiltersFromQuery = (parsed_query: BookmarksQueryParams): BookmarksFiltersProps => {
  return {
    next: extractFromArray(parsed_query.next || []) as string,
    prev: extractFromArray(parsed_query.next || []) as string
  };
};
const BookmarksView: React.FC = () => {
  const $ = _c(66);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    has_connection
  } = useContext(DashboardContext);
  const theme = useTheme();
  const {
    addToast
  } = useToasts();
  const {
    query,
    updateSearchQuery
  } = useSearchQuery();
  const t0 = query.selected_event_id || "";
  let t1;
  if ($[0] !== t0) {
    t1 = extractFromArray(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selected_event_id = t1 as string;
  const t2 = query.selected_id || "";
  let t3;
  if ($[2] !== t2) {
    t3 = extractFromArray(t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const selected_id = t3 as string;
  const filters = extractFiltersFromQuery(query);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = Date.now();
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const [event_list_render_key, setRenderKey] = useState(t4);
  let t5;
  if ($[5] !== updateSearchQuery) {
    t5 = selected_id_0 => updateSearchQuery({
      selected_id: selected_id_0
    }, {
      remove_keys: ["selected_event_id"]
    });
    $[5] = updateSearchQuery;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const onEntrySelected = t5;
  let t6;
  if ($[7] !== updateSearchQuery) {
    t6 = selected_event_id_0 => updateSearchQuery({
      selected_event_id: selected_event_id_0
    });
    $[7] = updateSearchQuery;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const onEventSelected = t6;
  let t7;
  if ($[9] !== updateSearchQuery) {
    t7 = attempt_id => updateSearchQuery({
      selected_attempt_id: attempt_id
    });
    $[9] = updateSearchQuery;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const onAttemptSelected = t7;
  const onFilterChanged = new_filters => {
    const should_update = Object.entries(new_filters).some(t8 => {
      const [key, value] = t8;
      return JSON.stringify(filters[key]) !== JSON.stringify(value);
    });
    if (should_update) {
      updateSearchQuery(new_filters, {
        remove_keys: ["selected_id", "selected_event_id"]
      });
    }
  };
  let t9;
  if ($[11] !== HookdeckAPI.bookmarks || $[12] !== filters) {
    t9 = () => HookdeckAPI.bookmarks.list(filters);
    $[11] = HookdeckAPI.bookmarks;
    $[12] = filters;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const {
    data,
    mutate
  } = useSWR(APIMethodKeys.bookmarks.list(filters), t9);
  let entry;
  let t10;
  let t11;
  if ($[14] !== data || $[15] !== selected_id) {
    entry = selected_id && data?.models ? data.models.find(event => event.id === selected_id) : null;
    t10 = useSWR;
    t11 = entry && APIMethodKeys.events.list({
      event_data_id: entry.event_data_id,
      limit: 10
    });
    $[14] = data;
    $[15] = selected_id;
    $[16] = entry;
    $[17] = t10;
    $[18] = t11;
  } else {
    entry = $[16];
    t10 = $[17];
    t11 = $[18];
  }
  let t12;
  if ($[19] !== HookdeckAPI.events || $[20] !== entry?.event_data_id) {
    t12 = () => HookdeckAPI.events.list({
      event_data_id: entry?.event_data_id,
      limit: 10
    });
    $[19] = HookdeckAPI.events;
    $[20] = entry?.event_data_id;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      refreshInterval: 10000,
      revalidateOnFocus: true
    };
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const {
    data: events,
    mutate: mutateEvents
  } = t10(t11, t12, t13);
  let t14;
  if ($[23] !== HookdeckAPI.bookmarks || $[24] !== addToast || $[25] !== events || $[26] !== mutate || $[27] !== mutateEvents) {
    t14 = bookmark => {
      HookdeckAPI.bookmarks.trigger(bookmark.id).then(new_events => {
        if (new_events.length === 0) {
          addToast("error", "No events created, is your connection filters excluding your bookmarked data?");
        } else {
          if (events) {
            mutateEvents({
              ...events,
              models: [...(new_events as EventWithData[]), ...events.models]
            }, false);
          }
          mutate(data_0 => {
            if (data_0) {
              const index = data_0.models.findIndex(m => m.id === bookmark.id);
              data_0.models[index] = {
                ...bookmark,
                last_used_at: new_events[0].created_at
              };
              return data_0;
            }
          });
          addToast("success", "Bookmark replayed");
        }
      }).catch(() => {
        addToast("error", "Bookmark failed to replay");
      });
    };
    $[23] = HookdeckAPI.bookmarks;
    $[24] = addToast;
    $[25] = events;
    $[26] = mutate;
    $[27] = mutateEvents;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const onReplay = t14;
  let t15;
  if ($[29] !== mutate) {
    t15 = () => {
      mutate();
    };
    $[29] = mutate;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== event_list_render_key) {
    t16 = [event_list_render_key];
    $[31] = event_list_render_key;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  useEffect(t15, t16);
  let t17;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <ViewTitle title="Bookmarks" tooltip="Bookmarks allow you to store and replay specific requests. They are most useful as a library of representative scenarios with which to develop and test your code." link={LINKS.product_docs.bookmarks} />;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== setRenderKey || $[35] !== updateSearchQuery) {
    t18 = <StyledViewNav><StyledViewNavHeader>{t17}<StyledViewNavActions><Button icon="reload" outline={true} onClick={() => {
            updateSearchQuery({}, {
              remove_keys: ["prev", "next", "selected_event_id", "id"]
            });
            setRenderKey(Date.now());
          }}>Refresh</Button></StyledViewNavActions></StyledViewNavHeader></StyledViewNav>;
    $[34] = setRenderKey;
    $[35] = updateSearchQuery;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== data || $[38] !== event_list_render_key || $[39] !== events || $[40] !== has_connection || $[41] !== onEntrySelected || $[42] !== onEventSelected || $[43] !== onFilterChanged || $[44] !== onReplay || $[45] !== selected_event_id || $[46] !== selected_id || $[47] !== theme) {
    t19 = has_connection ? <BookmarksList key={event_list_render_key} bookmarks={data} onEventSelected={onEventSelected} onBookmarkSelected={onEntrySelected} selected_id={selected_id} selected_event_id={selected_event_id} onFilterChanged={onFilterChanged} onReplay={onReplay} events={events} /> : <EmptyState title="Store and replay key events" description={"Bookmarks allow you to store and replay specific events, and can be used as a library of scenarios with which to test your code. To bookmark an event, you\u2019ll first need to create a connection."} asset={`bookmarks-${theme.mode}.png`} asset_position="background" cta={{
      label: "Create first connection",
      icon: "connections",
      to: "/create-first-connection"
    }} />;
    $[37] = data;
    $[38] = event_list_render_key;
    $[39] = events;
    $[40] = has_connection;
    $[41] = onEntrySelected;
    $[42] = onEventSelected;
    $[43] = onFilterChanged;
    $[44] = onReplay;
    $[45] = selected_event_id;
    $[46] = selected_id;
    $[47] = theme;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t18 || $[50] !== t19) {
    t20 = <StyledViewContent>{t18}{t19}</StyledViewContent>;
    $[49] = t18;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== mutate || $[53] !== onReplay || $[54] !== selected_event_id || $[55] !== selected_id || $[56] !== updateSearchQuery) {
    t21 = selected_id && !selected_event_id && <BookmarkDetails bookmark_id={selected_id} onUpdate={bookmark_0 => {
      mutate(data_1 => {
        const index_0 = data_1.models.findIndex(m_0 => m_0.id === bookmark_0.id);
        data_1.models[index_0] = bookmark_0;
        return data_1;
      });
    }} onReplay={onReplay} onDelete={id => {
      mutate(data_2 => {
        const models = data_2.models.filter(m_1 => m_1.id === id);
        return {
          ...data_2,
          count: models.length,
          pagination: {
            ...data_2,
            next: models[models.length - 1].id,
            prev: models[0].id
          },
          models
        };
      });
    }} onClose={() => updateSearchQuery({}, {
      remove_keys: ["selected_event_id", "selected_id"]
    })} />;
    $[52] = mutate;
    $[53] = onReplay;
    $[54] = selected_event_id;
    $[55] = selected_id;
    $[56] = updateSearchQuery;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== onAttemptSelected || $[59] !== selected_event_id || $[60] !== updateSearchQuery) {
    t22 = selected_event_id && <EventSidebar event_id={selected_event_id} onSelectedAttempt={onAttemptSelected} onClose={() => updateSearchQuery({}, {
      remove_keys: ["selected_event_id", "selected_id"]
    })} />;
    $[58] = onAttemptSelected;
    $[59] = selected_event_id;
    $[60] = updateSearchQuery;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  let t23;
  if ($[62] !== t20 || $[63] !== t21 || $[64] !== t22) {
    t23 = <StyledViewLayout>{t20}{t21}{t22}</StyledViewLayout>;
    $[62] = t20;
    $[63] = t21;
    $[64] = t22;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  return t23;
};
export default BookmarksView;